/**
 * The menu, as somebody who has to change it sees it.
 *
 * ---------------------------------------------------------------------
 * THE FIRST COLUMN IS WHETHER IT CAN BE SOLD
 *
 * An item with no price in any book cannot be rung — POS-3 refuses it —
 * and until this screen existed the only way to find that out was to tap
 * it on the terminal during service, in front of a member. So "Sellable"
 * is not a status badge tucked at the end of the row; it is the thing the
 * list is sorted and filtered by, and an unsellable item says what it is
 * missing rather than showing a red dot.
 *
 * ---------------------------------------------------------------------
 * A PRICE IS A HISTORY, NOT A FIELD
 *
 * There is no editable price cell, because there is no editable price.
 * `fb_price` is effective-dated and POS-3 allows one price per item, book,
 * outlet and day, so the control is "change it from a date" and what it
 * shows underneath is every price the item has had and every one it is
 * going to have. That is the answer to "why did the club sandwich go up",
 * which is a question a member asks and a manager should not have to
 * guess at.
 *
 * The date defaults to today and cannot be in the past. The screen says
 * why rather than greying the field: anything already rung kept the price
 * it was rung at, so backdating would change what the club believes it
 * charged and nothing else.
 *
 * ---------------------------------------------------------------------
 * EIGHTY-SIXING IS NOT EDITING THE MENU
 *
 * Running out of halibut at seven in the evening is a different act from
 * taking the halibut off the menu, done by a different person under
 * different pressure. It is one tap per menu entry, it needs only
 * `staff`, and it lives at the top of the page rather than buried in the
 * menu editor — because the person doing it is not editing anything.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { money, parseMoney, date as fmtDate } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status, Tile } from '../components/ui.tsx';

interface Item {
  id: string;
  code: string;
  name: string;
  description: string | null;
  revenue_category: string;
  prep_station: string | null;
  is_alcohol: boolean;
  needs_authorization: boolean;
  is_active: boolean;
  sort_order: number;
  live_prices: number;
  scheduled_prices: number;
  on_menus: number;
  modifier_groups: number;
  has_been_rung: boolean;
  sellable: boolean;
}

interface Price {
  id: string;
  price_book_id: string;
  price_book_name: string;
  outlet_id: string | null;
  outlet_name: string | null;
  amount_minor: number;
  starts_on: string;
  ends_on: string | null;
  in_force: boolean;
  scheduled: boolean;
}

interface PriceBook {
  id: string;
  code: string;
  name: string;
  applies_to: string;
  is_default: boolean;
}

interface MenuRow {
  id: string;
  code: string;
  name: string;
  serves_from: string | null;
  serves_until: string | null;
  is_active: boolean;
  entries: number;
  eighty_sixed: number;
  outlets: { outlet_id: string; name: string; is_default: boolean }[];
}

const today = () => new Date().toISOString().slice(0, 10);

export function FnbMenuPage() {
  const [open, setOpen] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const items = useApi<{ items: Item[] }>(
    `/v1/fnb/items?include_retired=${showRetired}`,
    [showRetired],
  );
  const books = useApi<{ price_books: PriceBook[] }>('/v1/fnb/price-books');
  const menus = useApi<{ menus: MenuRow[] }>('/v1/fnb/menu-admin');

  if (items.loading && !items.data) return <Loading />;

  const all = items.data?.items ?? [];
  const unsellable = all.filter((i) => i.is_active && !i.sellable);
  const eightySixed = (menus.data?.menus ?? []).reduce((n, m) => n + m.eighty_sixed, 0);

  return (
    <>
      <h1>The Menu</h1>
      <ErrorBanner error={items.error ?? books.error ?? menus.error} />

      <p className="muted small">
        Items are club-wide: the club sandwich sold at the bar and at the pool is one item on
        two menus at two prices, not two items. A price is never edited — it ends and a new one
        starts — so this screen changes prices from a date and keeps what they were.
      </p>

      <div className="tiles">
        <Tile label="Items" value={String(all.filter((i) => i.is_active).length)} />
        <Tile
          label="Cannot be sold"
          value={String(unsellable.length)}
          tone={unsellable.length > 0 ? 'warning' : 'good'}
          note={unsellable.length > 0 ? 'no price in any book' : 'everything has a price'}
        />
        <Tile
          label="Eighty-sixed today"
          value={String(eightySixed)}
          note={eightySixed > 0 ? 'off a menu for today only' : undefined}
        />
        <Tile label="Menus" value={String((menus.data?.menus ?? []).length)} />
      </div>

      {/* The one thing on this page that is not editing. */}
      {(menus.data?.menus ?? []).length > 0 && (
        <EightySix menus={menus.data?.menus ?? []} onChanged={() => menus.reload()} />
      )}

      {unsellable.length > 0 && (
        <Card title={`${unsellable.length} cannot be rung`}>
          <p className="muted small">
            These have no price in any book, so the terminal greys them out and ringing one
            would be refused. Set a price and they appear.
          </p>
          <ul>
            {unsellable.map((i) => (
              <li key={i.id}>
                <button className="linkish" onClick={() => setOpen(i.id)}>
                  {i.name}
                </button>{' '}
                <span className="muted small">· {i.code}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card
        title="Items"
        actions={
          <label className="inline">
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />{' '}
            Show retired
          </label>
        }
      >
        <div className="table">
          <table className="data data--cards">
            <thead>
              <tr>
                <th>Item</th>
                <th>Sells as</th>
                <th>Station</th>
                <th className="num">Menus</th>
                <th>Sellable</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {all.map((i) => (
                <tr key={i.id} className={i.is_active ? undefined : 'muted'}>
                  <td data-label="Item" className="cell--title">
                    {i.name}
                    <span className="muted small"> · {i.code}</span>
                    {i.is_alcohol && <span className="muted small"> · alcohol</span>}
                    {!i.is_active && <span className="muted small"> · retired</span>}
                  </td>
                  <td data-label="Sells as" className="muted">
                    {i.revenue_category}
                  </td>
                  <td data-label="Station" className="muted">
                    {i.prep_station ?? '—'}
                  </td>
                  <td data-label="Menus" className="num">
                    {i.on_menus}
                  </td>
                  <td data-label="Sellable">
                    {i.sellable ? (
                      <Status tone="good">
                        {i.live_prices} price{i.live_prices === 1 ? '' : 's'}
                        {i.scheduled_prices > 0 && `, ${i.scheduled_prices} scheduled`}
                      </Status>
                    ) : (
                      <Status tone="warning">No price</Status>
                    )}
                  </td>
                  <td className="cell--action">
                    <button
                      className="linkish"
                      onClick={() => setOpen(open === i.id ? null : i.id)}
                    >
                      {open === i.id ? 'Close' : 'Prices'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {all.length === 0 && (
          <Empty>Nothing on the menu yet. An item is one thing that can be rung up.</Empty>
        )}
      </Card>

      {open && (
        <ItemPrices
          itemId={open}
          books={books.data?.price_books ?? []}
          onChanged={() => items.reload()}
        />
      )}
    </>
  );
}

// =====================================================================
// One item's prices
// =====================================================================

function ItemPrices({
  itemId,
  books,
  onChanged,
}: {
  itemId: string;
  books: PriceBook[];
  onChanged: () => void;
}) {
  const { currency } = useSession();
  const { data, error, loading, reload } = useApi<{
    item: Item;
    prices: Price[];
  }>(`/v1/fnb/items/${itemId}`, [itemId]);

  const [form, setForm] = useState({
    price_book_id: '',
    amount: '',
    from: today(),
  });

  const set = useMutation(async () => {
    const minor = parseMoney(form.amount, currency);
    if (minor == null) throw new Error('that is not an amount');
    const r = await api(`/v1/fnb/items/${itemId}/price`, {
      method: 'POST',
      body: {
        price_book_id: form.price_book_id || books.find((b) => b.is_default)?.id,
        amount_minor: minor,
        from: form.from,
      },
    });
    setForm((f) => ({ ...f, amount: '' }));
    reload();
    onChanged();
    return r;
  });

  if (loading && !data) return <Loading rows={2} />;
  if (!data) return null;

  const { item, prices } = data;
  const live = prices.filter((p) => p.in_force);
  const past = prices.filter((p) => !p.in_force && !p.scheduled);
  const ahead = prices.filter((p) => p.scheduled);

  return (
    <Card title={`${item.name} — what it costs`}>
      <ErrorBanner error={error ?? set.error} />

      {live.length === 0 ? (
        <Empty>
          No price in force, so this cannot be rung. Set one below and it appears on the
          terminal.
        </Empty>
      ) : (
        <div className="table">
          <table className="data">
            <thead>
              <tr>
                <th>Book</th>
                <th>Where</th>
                <th className="num">Price</th>
                <th>Since</th>
              </tr>
            </thead>
            <tbody>
              {live.map((p) => (
                <tr key={p.id}>
                  <td>{p.price_book_name}</td>
                  <td className="muted">{p.outlet_name ?? 'Anywhere in the club'}</td>
                  <td className="num">{money(p.amount_minor, currency)}</td>
                  <td className="muted">{fmtDate(p.starts_on)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ahead.length > 0 && (
        <>
          <h3>Already Scheduled</h3>
          <ul className="muted small">
            {ahead.map((p) => (
              <li key={p.id}>
                {p.price_book_name}
                {p.outlet_name ? ` at ${p.outlet_name}` : ''} goes to{' '}
                {money(p.amount_minor, currency)} on {fmtDate(p.starts_on)}
              </li>
            ))}
          </ul>
        </>
      )}

      <h3>Change It</h3>
      <div className="grid-2">
        <label className="field">
          <span>Book</span>
          <select
            value={form.price_book_id}
            onChange={(e) => setForm((f) => ({ ...f, price_book_id: e.target.value }))}
          >
            {books.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
                {b.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>New price</span>
          <input
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            placeholder="14.50"
            inputMode="decimal"
          />
        </label>
        <label className="field">
          <span>From</span>
          <input
            type="date"
            min={today()}
            value={form.from}
            onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))}
          />
          <div className="hint">
            Not before today. Anything already rung kept the price it was rung at, so a
            backdated price would change what the club believes it charged and nothing else.
            {ahead.length > 0 && ' A rise already scheduled after this date is kept.'}
          </div>
        </label>
      </div>
      <div className="actions">
        <button
          disabled={!form.amount.trim() || set.busy}
          onClick={() => set.run()}
        >
          {set.busy ? 'Setting…' : 'Set the price'}
        </button>
      </div>

      {past.length > 0 && (
        <>
          <h3>What It Used to Cost</h3>
          <div className="table">
            <table className="data">
              <thead>
                <tr>
                  <th>Book</th>
                  <th className="num">Price</th>
                  <th>From</th>
                  <th>Until</th>
                </tr>
              </thead>
              <tbody>
                {past.map((p) => (
                  <tr key={p.id} className="muted">
                    <td>{p.price_book_name}</td>
                    <td className="num">{money(p.amount_minor, currency)}</td>
                    <td>{fmtDate(p.starts_on)}</td>
                    <td>{p.ends_on ? fmtDate(p.ends_on) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

// =====================================================================
// Eighty-six
// =====================================================================

function EightySix({ menus, onChanged }: { menus: MenuRow[]; onChanged: () => void }) {
  const [menuId, setMenuId] = useState('');
  const chosen = menuId || menus[0]?.id || '';

  const entries = useApi<{
    entries: { entry_id: string; item_name: string; is_available: boolean }[];
  }>(chosen ? `/v1/fnb/menus/${chosen}/entries-availability` : '', [chosen]);

  const toggle = useMutation(async (args: { entryId: string; available: boolean }) => {
    const r = await api(`/v1/fnb/menu-entries/${args.entryId}/availability`, {
      method: 'PATCH',
      body: { is_available: args.available },
    });
    entries.reload();
    onChanged();
    return r;
  });

  const rows = entries.data?.entries ?? [];

  return (
    <Card title="Run Out of Something?">
      <p className="muted small">
        Takes it off this menu for today. It stays on every report and comes back when you put
        it back — this is not retiring the item.
      </p>
      <ErrorBanner error={entries.error ?? toggle.error} />

      {menus.length > 1 && (
        <label className="field">
          <span>Menu</span>
          <select value={chosen} onChange={(e) => setMenuId(e.target.value)}>
            {menus.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.eighty_sixed > 0 ? ` · ${m.eighty_sixed} off` : ''}
              </option>
            ))}
          </select>
        </label>
      )}

      {entries.loading && !entries.data ? (
        <Loading rows={1} />
      ) : rows.length === 0 ? (
        <Empty>Nothing on this menu yet.</Empty>
      ) : (
        <div className="actions actions--wrap">
          {rows.map((e) => (
            <button
              key={e.entry_id}
              className={e.is_available ? 'secondary small' : 'small'}
              disabled={toggle.busy}
              onClick={() => toggle.run({ entryId: e.entry_id, available: !e.is_available })}
            >
              {e.item_name}
              {!e.is_available && ' · back on'}
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
